import { useCallback } from 'react';
import { IntPropertyDTO } from 'business/models/Models';
import { useSystemProperty } from 'CustomHooks/useSystemProperty';
import {
  applyPrecision,
  defaultPrecisionSettings,
  EPrecisionType,
  getPrecisionPropertyNames,
  PrecisionFormatter,
  PrecisionSetting,
} from 'components/plateviewer/render/plate/plateviewer/constants';

const usePrecisionSetting = (type: EPrecisionType): PrecisionSetting => {
  const propertyNames = getPrecisionPropertyNames(type);
  const significantDigits = useSystemProperty<IntPropertyDTO>(
    propertyNames.significantDigits,
    defaultPrecisionSettings[type].significantDigits,
  );
  const precision = useSystemProperty<IntPropertyDTO>(
    propertyNames.precision,
    defaultPrecisionSettings[type].precision,
  );
  return { significantDigits, precision };
};

/**
 * Loads the Experiment precision / significant digits system properties and returns a
 * formatter that applies them by type. Call once per plate and pass the formatter down,
 * rather than per well, since each system property hook opens its own broadcast channel.
 */
export const usePrecisionFormatter = (): PrecisionFormatter => {
  const concentration = usePrecisionSetting(EPrecisionType.Concentration);
  const volume = usePrecisionSetting(EPrecisionType.Volume);
  const pH = usePrecisionSetting(EPrecisionType.PH);

  return useCallback(
    (value: number, type: EPrecisionType) => {
      const settings = {
        [EPrecisionType.Concentration]: concentration,
        [EPrecisionType.Volume]: volume,
        [EPrecisionType.PH]: pH,
      };
      const { significantDigits, precision } = settings[type];
      return applyPrecision(value, significantDigits, precision);
    },
    [
      concentration.significantDigits,
      concentration.precision,
      volume.significantDigits,
      volume.precision,
      pH.significantDigits,
      pH.precision,
    ],
  );
};
